import { ClerkLoaded, ClerkLoading, ClerkProvider, Protect } from "@clerk/nextjs";
import { ChannelProvider } from "ably/react";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import { publicPaths, SILENT_TOASTS_KEY } from "@remi/constants";
import { getEnvPublic, isLocalDevelopmentEnvironment, isProductionEnvironment } from "@remi/environment";
import { RemiFontPrimary } from "@remi/next-ui/fonts";
import { ThemeProvider } from "@remi/next-ui/providers";

import { InboxContextProvider } from "~/contexts/InboxContext";
import { NotificationsProvider } from "~/contexts/NotificationsContext";

import "../styles/web-v4.css";
import type { ReactElement, ReactNode } from "react";
import type { NextPage } from "next";
import type { AppProps, AppType } from "next/app";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import { QueryClientProvider } from "@tanstack/react-query";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useLocalStorage } from "usehooks-ts";
import { RemiMaintenance } from "@remi/next-ui";
import { useDebugMode, useInactiveTimeout } from "@remi/next-ui/hooks";
import { System } from "@remi/types/system";
import { Theme } from "@remi/types/theme";
import { installClientPolyfills } from "@remi/utils/install-client-polyfills";

import { api, queryClient } from "~/utils/api";
import { ErrorBoundary } from "~/components/ErrorBoundary";
import Loading from "~/components/Loading";
import { CommandMenuProvider } from "~/components/command-menu/CommandMenuProvider";
import { Toaster } from "~/components/ui/sonner";
import { AttemptTrackerProvider } from "~/contexts/AttemptTrackerContext";
import { CompletionTrackerProvider } from "~/contexts/CompletionTrackerContext";
import { ContactDrawerWrapper } from "~/contexts/ContactDrawerProvider";
import { PopoutManagerProvider } from "~/contexts/PopoutManagerProvider";
import { StartAgreementDrawerProvider } from "~/contexts/StartAgreementDrawerProvider";
import { UserProfileProvider } from "~/contexts/UserProfileContext";
import { AblyWrapper } from "~/contexts/WsContext";
import { useAppSettings } from "~/hooks/useAppSettings";
import { useCapturePwaInstall } from "~/hooks/useCapturePwaInstall";
import { useDismissToastsShortcut } from "~/hooks/useDismissToastsShortcut";
import { usePostHogPageContext } from "~/hooks/usePostHogPageContext";
import { useVersionSync } from "~/hooks/useVersionSync";
import { ImpersonationWarning } from "../components/user/ImpersonationWarning";
import { MsaResignBanner } from "../components/contractors/MsaResignBanner";
import { MfaEnrollmentBanner } from "../components/user/mfa/MfaEnrollmentBanner";

installClientPolyfills();

// Vercel packages _app's server trace into every function, API routes included. The
// typeof-window branch is folded away on the server, so Turbopack never traces the
// import; ssr: false alone still traces it.
const PopoutViewport = dynamic(
  () =>
    typeof window === "undefined"
      ? Promise.resolve(() => null)
      : // oxlint-disable-next-line remi/no-async-import -- client-only; keeps the popout graph out of every server function
        import("~/components/popout/PopoutViewport").then((m) => m.PopoutViewport),
  { ssr: false },
);

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

// Initialize PostHog
if (typeof window !== "undefined") {
  posthog.init(getEnvPublic("NEXT_PUBLIC_POSTHOG_API_KEY"), {
    api_host: getEnvPublic("NEXT_PUBLIC_POSTHOG_HOST"),
    disable_surveys: true,
    capture_dead_clicks: true,
    // usePostHogPageContext owns pageviews so every one carries appArea.
    // capture_pageleave defaults to following capture_pageview off.
    capture_pageview: false,
    capture_pageleave: true,
  });
}

// Clerk validation REPLACES its default allowlist when this prop is non-empty
// (see @clerk/clerk-js createAllowedRedirectOrigins). We only need these
// patterns under Portless + local dev, where the app serves from dynamic
// `.localhost` hostnames that Clerk's defaults don't know about. In prod,
// passing this prop would narrow Clerk's trust to localhost-only and break
// cross-origin redirect validation — so gate on isLocalDevelopmentEnvironment
// and pass undefined in prod/preview to restore Clerk's default behavior.
const ALLOWED_REDIRECT_ORIGINS = isLocalDevelopmentEnvironment()
  ? [
      "https://*.localhost",
      "https://*.localhost:*",
      "http://*.localhost",
      "http://*.localhost:*",
      "https://localhost:*",
      "http://localhost:*",
    ]
  : undefined;

const CLERK_PUBLISHABLE_KEY = getEnvPublic("NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY");
// Must match @clerk/clerk-js in pnpm-lock.yaml — update when upgrading @clerk/nextjs
const CLERK_JS_VERSION = "5.125.10";

interface AppSettingsGateProps {
  children: ReactNode;
  isPending: boolean;
  apiVersion: string | undefined;
  isDisabled: boolean | undefined;
}

// Rendered inside ClerkProvider so clerk-js downloads and loads while the config queries are still in flight.
const AppSettingsGate = ({ children, isPending, apiVersion, isDisabled }: AppSettingsGateProps) => {
  if (isPending) {
    return (
      <div className="h-screen w-screen max-sm:h-dvh">
        <Loading />
      </div>
    );
  }
  if (!apiVersion || isDisabled === undefined) {
    return null;
  }
  return isDisabled ? <RemiMaintenance /> : children;
};

const MyApp: AppType = ({ Component, pageProps }: AppPropsWithLayout) => {
  const router = useRouter();
  const { data: version } = useVersionSync();
  const { data: appSettings, isPending } = useAppSettings();
  const [toastsDisabled] = useLocalStorage(SILENT_TOASTS_KEY, false);
  const showToasts = !toastsDisabled || isProductionEnvironment();
  const getLayout = Component.getLayout ?? ((page) => page);
  useDebugMode();
  useInactiveTimeout();
  useDismissToastsShortcut();
  usePostHogPageContext();
  useCapturePwaInstall();

  const rootClassName = `${RemiFontPrimary.className} ${RemiFontPrimary.variable} flex h-screen max-sm:h-dvh has-[[data-fit-visible-viewport]]:h-dvh w-screen overflow-hidden [&>.swagger-ui]:overflow-auto flex-col `;
  // Define --font-primary on :root so portaled content (Radix popovers, modals, tooltips)
  // rendered at document.body — outside the font wrapper — inherits the brand font instead
  // of the system fallback. The next/font CSS module can't be imported in _document. The
  // key lets next/head dedupe the <style> if both branches' Heads ever coexist (e.g. HMR).
  const fontVariableHead = (
    <Head>
      <style key="font-primary-css-var">{`:root { --font-primary: ${RemiFontPrimary.style.fontFamily}; }`}</style>
      {!isProductionEnvironment() && (
        <link key="favicon-preview" rel="icon" type="image/svg+xml" href="/favicon-preview.svg" />
      )}
      <link key="manifest" rel="manifest" href="/site.webmanifest" />
      <link key="apple-touch-icon" rel="apple-touch-icon" href="/apple-touch-icon.png" />
      <meta key="mobile-web-app-capable" name="mobile-web-app-capable" content="yes" />
      <meta key="apple-mobile-web-app-capable" name="apple-mobile-web-app-capable" content="yes" />
      <meta key="apple-mobile-web-app-title" name="apple-mobile-web-app-title" content="Remi" />
      <meta key="theme-color" name="theme-color" content="#ffffff" />
    </Head>
  );
  if (!CLERK_PUBLISHABLE_KEY.startsWith("pk_")) {
    return (
      <ThemeProvider defaultTheme={Theme.LIGHT} defaultSystem={System.CRM}>
        <div className="h-screen w-screen max-sm:h-dvh">
          <Loading />
        </div>
      </ThemeProvider>
    );
  }

  if (publicPaths.some((path) => router.pathname.startsWith(path))) {
    return (
      <ErrorBoundary>
        <ThemeProvider defaultTheme={Theme.LIGHT} defaultSystem={System.CRM}>
          <NuqsAdapter>
            <ClerkProvider
              {...pageProps}
              publishableKey={CLERK_PUBLISHABLE_KEY}
              allowedRedirectOrigins={ALLOWED_REDIRECT_ORIGINS}
              clerkJSVersion={CLERK_JS_VERSION}
              __unstable_invokeMiddlewareOnAuthStateChange={false}
            >
              {fontVariableHead}
              <div className={rootClassName}>
                <ImpersonationWarning />
                {getLayout(<Component {...pageProps} />)}
                <Toaster closeButton />
              </div>
            </ClerkProvider>
          </NuqsAdapter>
        </ThemeProvider>
      </ErrorBoundary>
    );
  } else {
    return (
      <ErrorBoundary>
        <ThemeProvider defaultTheme={Theme.LIGHT} defaultSystem={System.CRM}>
          <NuqsAdapter>
            <PostHogProvider client={posthog}>
              <ClerkProvider
                {...pageProps}
                publishableKey={CLERK_PUBLISHABLE_KEY}
                allowedRedirectOrigins={ALLOWED_REDIRECT_ORIGINS}
                clerkJSVersion={CLERK_JS_VERSION}
              >
                <AppSettingsGate
                  isPending={isPending}
                  apiVersion={version?.apiVersion}
                  isDisabled={appSettings?.disabled}
                >
                  <ClerkLoaded>
                    <Protect>
                      <QueryClientProvider client={queryClient}>
                        <UserProfileProvider>
                          <AblyWrapper>
                            <AttemptTrackerProvider>
                              <CompletionTrackerProvider>
                                {fontVariableHead}
                                <div className={rootClassName}>
                                  <NotificationsProvider>
                                    <ChannelProvider channelName="tickets">
                                      <InboxContextProvider>
                                        <PopoutManagerProvider>
                                          <ContactDrawerWrapper>
                                            <StartAgreementDrawerProvider>
                                              <CommandMenuProvider>
                                                <ImpersonationWarning />
                                                <MfaEnrollmentBanner />
                                                <MsaResignBanner />
                                                {getLayout(<Component {...pageProps} />)}
                                                {showToasts && <Toaster closeButton />}
                                                <PopoutViewport />
                                              </CommandMenuProvider>
                                            </StartAgreementDrawerProvider>
                                          </ContactDrawerWrapper>
                                        </PopoutManagerProvider>
                                      </InboxContextProvider>
                                    </ChannelProvider>
                                  </NotificationsProvider>
                                </div>
                              </CompletionTrackerProvider>
                            </AttemptTrackerProvider>
                          </AblyWrapper>
                        </UserProfileProvider>
                      </QueryClientProvider>
                    </Protect>
                  </ClerkLoaded>
                  <ClerkLoading>
                    <div className="h-screen w-screen max-sm:h-dvh">
                      <Loading />
                    </div>
                  </ClerkLoading>
                </AppSettingsGate>
              </ClerkProvider>
            </PostHogProvider>
            {
              // Uncomment to enable React Query Devtools
              // <ReactQueryDevtools initialIsOpen={false} position="bottom" buttonPosition="bottom-left" />
            }
          </NuqsAdapter>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }
};

export default api.withTRPC(MyApp);
